import { useState } from "react"

import { useMutation } from "@apollo/client"

import { useErrorNotification } from "@/souhl_admin/components/ui/ErrorNotification/ErrorNotification"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

const UpdateLineGroupAndCompanyMappings = graphql(/* GraphQL */ `
	mutation UpdateLineGroupAndCompanyMappingsForGeneralInfo(
		$input: UpdateLineGroupAndCompanyMappingsInput!
	) {
		updateLineGroupAndCompanyMappings(input: $input) {
			updatedCount
		}
	}
`)

type LineGroupInfo = {
	id: string
	name: string
}

// inlineError は入力起因 (重複 / 空文字) を入力欄直下に表示するために使い、
// addError は通信起因 (mutation 失敗) をトースト通知するために使う。
export const useLineGroupMappingsEdit = (
	companyId: string,
	currentLineGroups: readonly LineGroupInfo[]
) => {
	const { addError } = useErrorNotification()
	const [editing, setEditing] = useState(false)
	const [draftDisplayNames, setDraftDisplayNames] = useState<string[]>([])
	const [newDisplayName, setNewDisplayName] = useState("")
	const [inlineError, setInlineError] = useState<string | null>(null)

	const [updateMutation, { loading: saving }] = useMutation(
		UpdateLineGroupAndCompanyMappings,
		{
			refetchQueries: ["GetCompanyForGeneralInfo"],
			awaitRefetchQueries: true,
		}
	)

	// 既存の line_group は name (= bizdev.channel_groups.display_name) で初期表示する。
	// mutation 実行時に bizdev 側で display_name → line_group_id を解決する。
	const currentDisplayNames = currentLineGroups.map((lg) => lg.name)

	const startEdit = () => {
		setDraftDisplayNames([...currentDisplayNames])
		setNewDisplayName("")
		setInlineError(null)
		setEditing(true)
	}

	const cancelEdit = () => {
		setEditing(false)
		setDraftDisplayNames([])
		setNewDisplayName("")
		setInlineError(null)
	}

	const addDisplayName = () => {
		const trimmed = newDisplayName.trim()
		if (!trimmed) return
		if (draftDisplayNames.includes(trimmed)) {
			setInlineError(`グループ名 "${trimmed}" は既に追加されています`)
			return
		}
		setDraftDisplayNames([...draftDisplayNames, trimmed])
		setNewDisplayName("")
		setInlineError(null)
	}

	const removeDisplayName = (displayName: string) => {
		setDraftDisplayNames(draftDisplayNames.filter((x) => x !== displayName))
	}

	// 全置換 mutation のため、削除がある場合のみ確認ダイアログを出す。
	// 追加だけならソフトデリート発生せず安全なのでサイレント保存する。
	const save = () => {
		const currentSet = new Set(currentDisplayNames)
		const draftSet = new Set(draftDisplayNames)
		const added = draftDisplayNames.filter((dn) => !currentSet.has(dn))
		const removed = currentDisplayNames.filter((dn) => !draftSet.has(dn))

		if (added.length === 0 && removed.length === 0) {
			cancelEdit()
			return
		}

		if (removed.length > 0) {
			const lines = [
				"以下の LINE グループの紐付けを解除します。",
				`削除: ${removed.join(", ")}`,
			]
			if (added.length > 0) lines.push(`追加: ${added.join(", ")}`)
			lines.push("よろしいですか？")
			if (!window.confirm(lines.join("\n"))) return
		}

		setInlineError(null)
		updateMutation({
			variables: {
				input: {
					companyId,
					lineGroupDisplayNames: draftDisplayNames,
				},
			},
		})
			.then(() => {
				setEditing(false)
				setDraftDisplayNames([])
				setNewDisplayName("")
			})
			.catch((err: Error) => {
				addError(`LINE グループの更新に失敗しました: ${err.message}`)
			})
	}

	return {
		editing,
		draftDisplayNames,
		newDisplayName,
		inlineError,
		saving,
		setNewDisplayName,
		startEdit,
		cancelEdit,
		addDisplayName,
		removeDisplayName,
		save,
	}
}
